import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useTheme } from 'styled-components';
import WarningIcon from '@mui/icons-material/Warning';
import DoneIcon from '@mui/icons-material/Done';
import SearchIcon from '@mui/icons-material/Search';
import { ExpandLess, ExpandMore, PrintOutlined } from '@mui/icons-material';

import * as S from './styles';
import { ticketsServiceNewStudent } from '../../../../../services/newStudent';
import { ButtonSimple } from '../../../../../components/newStudent/Buttons';
import { AlertBar } from '../../../../../components/newStudent/Alert';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../components/newStudent/ProviderNewStudent';
import TableBasic from '../../../../../components/newStudent/Table';
import { handleMaxWidth } from '../../../../../util/newStudent';
import { formatDateHour } from '../../../../../util/common/appUtil';
import { getTicketStatusStyle, TicketAlertIconColor } from '../../../../../variables/ticket';
import {
  IInternalTicketDetail,
  IInternalTicketListItem,
} from '../../../../../interfaces/newStudent/ticket.interface';
import TicketDescription from '../TicketDescription';
import { getHistoricMovementNumber } from '../../../../../util/newStudent/ticket/historicMovementNumber';
import TicketActionModal, {
  TicketActionModalMode,
} from '../TicketActionModal';

interface Props {
  item: IInternalTicketListItem;
  index: number;
  indexOpen: number | undefined;
  setIndexOpen: Dispatch<SetStateAction<number | undefined>>;
  onRefreshList: () => void;
}

const statusIcons: Record<TicketAlertIconColor, React.ReactNode> = {
  danger: <WarningIcon fontSize="large" />,
  tertiary: <DoneIcon fontSize="large" />,
  warning: <SearchIcon fontSize="large" />,
  white: <SearchIcon fontSize="large" />,
};

export default function TicketItem({
  item,
  index,
  indexOpen,
  setIndexOpen,
  onRefreshList,
}: Props) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [ticket, setTicket] = useState<IInternalTicketDetail>();
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionModalMode, setActionModalMode] =
    useState<TicketActionModalMode>('action');
  const theme = useTheme();

  useEffect(() => {
    if (indexOpen !== index || ticket || !item.protocol) {
      return;
    }

    const loadTicketDetail = async () => {
      setIsLoading(true);
      try {
        const { result, data } = await ticketsServiceNewStudent.editShow(
          item.protocol,
        );
        if (result) {
          setTicket(data);
        }
      } catch (error) {
        throw new Error(String(error));
      } finally {
        setIsLoading(false);
      }
    };

    loadTicketDetail();
  }, [indexOpen, index, item.protocol, ticket, setIsLoading]);

  const handleOpen = async (protocol: string, ticketIndex: number) => {
    if (indexOpen === ticketIndex) {
      setIndexOpen(undefined);
      setTicket(undefined);
      setIsActionModalOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      setIndexOpen(ticketIndex);
      const { result, data } = await ticketsServiceNewStudent.editShow(protocol);
      if (result) {
        setTicket(data);
        setIsActionModalOpen(false);
      }
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const reloadTicketDetail = async () => {
    if (!item.protocol) return;
    const { result, data } = await ticketsServiceNewStudent.editShow(
      item.protocol,
    );
    if (result) {
      setTicket(data);
    }
  };

  const handleActionModalSuccess = async () => {
    await reloadTicketDetail();
    onRefreshList();
  };

  const handleOpenActionModal = (mode: TicketActionModalMode) => {
    setActionModalMode(mode);
    setIsActionModalOpen(true);
  };

  const tableData = {
    title: ['Movimentação'],
    content: ticket?.historic?.map((historicItem, historicIndex) => {
      const movementNumber = getHistoricMovementNumber(
        ticket.historic.length,
        historicIndex,
      );

      return {
        Movimentação: {
          item: (
            <S.ContentMovimentation>
              <S.MovementHeaderRow>
                <S.H6>
                  Movimentação n°{movementNumber} incluída por{' '}
                  {historicItem.userName ?? 'Usuário'} no dia{' '}
                  {formatDateHour(historicItem.createdDate)}
                </S.H6>
                {historicItem.attachments &&
                  historicItem.attachments.length > 0 && (
                    <S.AttachmentsEnd>
                      {historicItem.attachments.map((attachment) => (
                        <S.DivAnexos key={attachment.id}>
                          <S.AttachmentName>{attachment.filename}</S.AttachmentName>
                          <ButtonSimple
                            color="success"
                            heightSize="small"
                            widthSize="small"
                            onClick={() => window.open(attachment.url)}
                          >
                            <PrintOutlined fontSize="large" />
                          </ButtonSimple>
                        </S.DivAnexos>
                      ))}
                    </S.AttachmentsEnd>
                  )}
              </S.MovementHeaderRow>
              <div
                dangerouslySetInnerHTML={{ __html: historicItem.description }}
              />
            </S.ContentMovimentation>
          ),
        },
      };
    }),
  };

  const detailStatusStyle = getTicketStatusStyle(ticket?.statusName ?? item.status);
  const displayTitle = ticket?.title ?? item.title;
  const displayStatus = ticket?.statusName ?? item.status;
  const displayCreatedDate = ticket?.createdDate ?? item.createdDate;
  const displayUpdatedDate = ticket?.updatedDate ?? item.updatedDate;
  const isLightTextStatus = ['danger', 'warning'].includes(
    detailStatusStyle.alertColor,
  );

  const statusButtonStyles = isLightTextStatus
    ? {
        color: theme.colors?.backgroundColor,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.colors?.backgroundColor}`,
      }
    : undefined;

  return (
    <S.Container>
      <AlertBar
        noWarning
        noWarningIcon
        pointer
        onClick={() => handleOpen(item.protocol, index)}
        color={detailStatusStyle.alertColor}
        text={
          <S.AlertTitleRow>
            <S.ProtocolText $lightText={isLightTextStatus}>
              Protocolo nº {item.protocol}
            </S.ProtocolText>
            <S.DateText $lightText={isLightTextStatus}>
              Abertura: {formatDateHour(displayCreatedDate)}
            </S.DateText>
            <S.DateText $lightText={isLightTextStatus}>
              Última atualização: {formatDateHour(displayUpdatedDate)}
            </S.DateText>
            <S.TitleText $lightText={isLightTextStatus}>
              Assunto: {displayTitle}
            </S.TitleText>
          </S.AlertTitleRow>
        }
        icon={statusIcons[detailStatusStyle.iconColor]}
        iconColor={detailStatusStyle.iconColor}
        flexDirection={handleMaxWidth(theme.size?.medium) ? 'column' : 'row'}
        button={
          <S.Buttons>
            <ButtonSimple
              color={detailStatusStyle.buttonColor}
              outline
              noButton
              heightSize="small"
              widthSize="large"
              styles={statusButtonStyles}
            >
              {displayStatus}
            </ButtonSimple>
            <S.ToggleIcon $lightText={isLightTextStatus}>
              {index === indexOpen && ticket ? (
                <ExpandLess fontSize="medium" />
              ) : (
                <ExpandMore fontSize="medium" />
              )}
            </S.ToggleIcon>
          </S.Buttons>
        }
      />
      {index === indexOpen && ticket && (
        <S.ContainerFocus>
          {handleMaxWidth(theme.size?.medium) ? (
            <TicketDescription ticket={ticket} />
          ) : (
            <TableBasic data={tableData} />
          )}

          {(ticket.canEdit || ticket.canReopen) && (
            <S.ActionButtons>
              {ticket.canEdit && (
                <ButtonSimple
                  color="primary"
                  widthSize="medium"
                  onClick={() => handleOpenActionModal('action')}
                >
                  Nova resposta
                </ButtonSimple>
              )}
              {ticket.canReopen && (
                <ButtonSimple
                  color="primary"
                  outline
                  widthSize="medium"
                  onClick={() => handleOpenActionModal('reopen')}
                >
                  Reabrir ticket
                </ButtonSimple>
              )}
            </S.ActionButtons>
          )}

          <TicketActionModal
            isOpen={isActionModalOpen}
            setIsOpen={setIsActionModalOpen}
            mode={actionModalMode}
            protocol={ticket.protocol}
            onSuccess={handleActionModalSuccess}
          />
        </S.ContainerFocus>
      )}
    </S.Container>
  );
}
